import type { DailyAvailability, WeeklyAvailability } from './types';

export const AVAILABILITY_TIMEZONE = 'Africa/Johannesburg';

export type WeekdayKey = keyof WeeklyAvailability;

const WEEKDAY_MAP: Record<string, WeekdayKey> = {
  Mon: 'mon',
  Tue: 'tue',
  Wed: 'wed',
  Thu: 'thu',
  Fri: 'fri',
  Sat: 'sat',
  Sun: 'sun',
};

/** Day key for a Date in the given IANA timezone. */
export function weekdayKeyInTimeZone(
  date: Date,
  timeZone: string = AVAILABILITY_TIMEZONE,
): WeekdayKey {
  const short = new Intl.DateTimeFormat('en-US', { timeZone, weekday: 'short' }).format(date);
  const key = WEEKDAY_MAP[short];
  if (!key) throw new Error(`Unexpected weekday: ${short}`);
  return key;
}

/** Minutes from local midnight in timezone. */
export function minutesSinceMidnightInTimeZone(
  date: Date,
  timeZone: string = AVAILABILITY_TIMEZONE,
): number {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(date);
  const hour = Number(parts.find((p) => p.type === 'hour')?.value ?? 0);
  const minute = Number(parts.find((p) => p.type === 'minute')?.value ?? 0);
  // en-GB can yield "24" for midnight in some engines — normalise.
  const h = hour === 24 ? 0 : hour;
  return h * 60 + minute;
}

export function parseHmToMinutes(hm: string): number {
  const [hRaw, mRaw] = hm.split(':');
  const h = Number(hRaw);
  const m = Number(mRaw);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return NaN;
  return h * 60 + m;
}

export function formatMinutesToHm(total: number): string {
  const h = Math.floor(total / 60);
  const m = total % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

export function hasAnyWeeklyHours(week: WeeklyAvailability | null | undefined): boolean {
  if (!week) return false;
  return (Object.keys(week) as WeekdayKey[]).some((d) => (week[d]?.length ?? 0) > 0);
}

/**
 * True when [start, end) lies fully inside one of the seller's slots for that
 * local day (does not span midnight — bookings are same-day for v1).
 */
export function isWithinWeeklyHours(
  start: Date,
  end: Date,
  week: WeeklyAvailability,
  timeZone: string = AVAILABILITY_TIMEZONE,
): boolean {
  if (!(end.getTime() > start.getTime())) return false;
  if (weekdayKeyInTimeZone(start, timeZone) !== weekdayKeyInTimeZone(end, timeZone)) {
    return false;
  }
  const day = weekdayKeyInTimeZone(start, timeZone);
  const slots = week[day] ?? [];
  if (slots.length === 0) return false;

  const startMin = minutesSinceMidnightInTimeZone(start, timeZone);
  const endMin = minutesSinceMidnightInTimeZone(end, timeZone);

  return slots.some((slot) => {
    const a = parseHmToMinutes(slot.start);
    const b = parseHmToMinutes(slot.end);
    if (!Number.isFinite(a) || !Number.isFinite(b) || b <= a) return false;
    return startMin >= a && endMin <= b;
  });
}

/** Build a Date for local Y-M-D + HH:mm in timezone (via temporal offset probe). */
export function zonedLocalDateTime(
  year: number,
  monthIndex: number,
  day: number,
  hour: number,
  minute: number,
  timeZone: string = AVAILABILITY_TIMEZONE,
): Date {
  // Iterate: guess UTC, read back local parts, adjust — stable for SAST (no DST).
  const guess = new Date(Date.UTC(year, monthIndex, day, hour, minute, 0));
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(guess);
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value);
  let ph = get('hour');
  if (ph === 24) ph = 0;
  const asUtcMs =
    Date.UTC(year, monthIndex, day, hour, minute) -
    (Date.UTC(get('year'), get('month') - 1, get('day'), ph, get('minute')) -
      guess.getTime());
  return new Date(asUtcMs);
}

function ymdInTimeZone(
  date: Date,
  timeZone: string,
): { year: number; monthIndex: number; day: number } {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value);
  return { year: get('year'), monthIndex: get('month') - 1, day: get('day') };
}

/** Open start times for the next N local days (30-min steps by default). */
export function listOpenStartTimes(opts: {
  week: WeeklyAvailability;
  durationMinutes: number;
  from?: Date;
  daysAhead?: number;
  stepMinutes?: number;
  minLeadMinutes?: number;
  timeZone?: string;
}): Date[] {
  const {
    week,
    durationMinutes,
    from = new Date(),
    daysAhead = 14,
    stepMinutes = 30,
    minLeadMinutes = 30,
    timeZone = AVAILABILITY_TIMEZONE,
  } = opts;

  if (!hasAnyWeeklyHours(week) || durationMinutes <= 0) return [];

  const earliest = from.getTime() + minLeadMinutes * 60 * 1000;
  const out: Date[] = [];
  const base = ymdInTimeZone(from, timeZone);

  for (let d = 0; d < daysAhead; d++) {
    const cursor = new Date(Date.UTC(base.year, base.monthIndex, base.day + d, 12, 0, 0));
    const { year, monthIndex, day } = ymdInTimeZone(cursor, timeZone);
    const dayKey = weekdayKeyInTimeZone(
      zonedLocalDateTime(year, monthIndex, day, 12, 0, timeZone),
      timeZone,
    );
    const slots: DailyAvailability[] = week[dayKey] ?? [];

    for (const slot of slots) {
      const slotStart = parseHmToMinutes(slot.start);
      const slotEnd = parseHmToMinutes(slot.end);
      if (!Number.isFinite(slotStart) || !Number.isFinite(slotEnd) || slotEnd <= slotStart) {
        continue;
      }
      for (let t = slotStart; t + durationMinutes <= slotEnd; t += stepMinutes) {
        const h = Math.floor(t / 60);
        const m = t % 60;
        const start = zonedLocalDateTime(year, monthIndex, day, h, m, timeZone);
        if (start.getTime() < earliest) continue;
        out.push(start);
      }
    }
  }

  return out;
}

export function rangesOverlap(
  aStartMs: number,
  aEndMs: number,
  bStartMs: number,
  bEndMs: number,
): boolean {
  return aStartMs < bEndMs && bStartMs < aEndMs;
}

/** Statuses that count as a diary clash for the seller. */
export const CLASH_BOOKING_STATUSES = [
  'accepted',
  'in_progress',
  'awaiting_confirmation',
  'requested',
  'counter_proposed',
] as const;
